import { uniqBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

import deleteWishlistProduct from '@/common/api/wishlists/products/deleteWishlistProduct';
import Snack from '@/common/components/molecules/Snack/Snack';
import { useAddToContext } from '@/common/contexts/AddToContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import deleteWishlist from '@/ebat/api/wishlists/deleteWishlist';
import patchWishlist from '@/ebat/api/wishlists/patchWishlist';
import patchWishlistItems from '@/ebat/api/wishlists/patchWishlistItems';
import patchWishlistToggleSelection from '@/ebat/api/wishlists/patchWishlistToggleSelection';
import putWishlist from '@/ebat/api/wishlists/putWishlist';

/**
 * Manage a wishlist :
 * - Add product(s) to wishlist
 * - Remove product(s) to wishlist
 *
 * @param isLoading
 * @param disabled
 * @param wishlist
 */
const useWishlist = ({ disabled, isLoading, wishlist }) => {
  const { t } = useTranslation();
  const { setModalIsOpen } = useModalContext();

  const {
    addToWishlist,
    isLoading: isLoadingAddToWishlist,
  } = useAddToContext();

  // TODO : Make the order of the items to pass it to localWishlist to have a clean local
  const [localWishlist, setLocalWishlist] = useState(wishlist);

  const isUpdateOrderNeeded = useMemo(() => {
    if (wishlist && localWishlist.items.length > 1 && localWishlist.drag) {
      const originalSort = wishlist.items
        .map((item) => `${item.id}${item.position}`)
        .join('');
      const localSort = localWishlist.items
        .map((item) => `${item.id}${item.position}`)
        .join('');

      return originalSort !== localSort;
    }

    return false;
  }, [localWishlist, wishlist]);

  // Use effect generate a side effect when wishlist is changing
  useEffect(() => {
    setLocalWishlist(wishlist);
  }, [wishlist]);

  /**
   * update localWishlist state with the updated product
   * @param {object} product product selected
   */
  const updateItem = useCallback(
    (product) => {
      setLocalWishlist({
        ...localWishlist,
        items: localWishlist.items.map((item) => {
          if (item.id === product.id) {
            return {
              ...item,
              is_selected: product.is_selected,
              quantity: product.quantity,
            };
          }

          return item;
        }),
      });
    },
    [localWishlist],
  );

  /**
   * Merge results with existing products updated
   * and update local state with the new set of products
   * @param {array} products for API call response
   */
  const updateList = useCallback(
    (products) => {
      setLocalWishlist({
        ...localWishlist,
        items: uniqBy([...products, ...localWishlist.items], 'id'),
      });
    },
    [localWishlist],
  );

  /**
   * adds products to the wishlist with addToWishlist from addTo context
   * @param {object} products from quick entry
   * @type {function(*): void}
   */
  const onAddProducts = useCallback(
    (products) => {
      const formattedProduct = products.reduce((acc, item) => {
        acc.push({
          // eslint-disable-next-line no-nested-ternary
          quantity: item.quantity
            ? item.quantity
            : item.sales_packaging && !item.split_packaging
            ? item.sales_packaging
            : 1,
          sku: item.sku || item.main_sku,
          position: 0,
        });

        return acc;
      }, []);

      if (formattedProduct.length > 0) {
        addToWishlist({
          wishlist: { ...localWishlist },
          items: [...formattedProduct],
          onSuccessCallback: (data) => {
            updateList(data);
            setModalIsOpen(false);
          },
          options: {}, // TODO: to set
        });
      }
    },
    [addToWishlist, localWishlist, setModalIsOpen, updateList],
  );

  /**
   * Add items to the wishlist from product list or simple product
   * @param {object} targetWishlist wishlist target
   * @param {array} items products list
   * @type {(function({wishlist: *, items: *}): void)|*}
   */
  const onAddProductsToWishlist = useCallback(
    ({
      wishlist: targetWishlist,
      items: itemList,
      onSuccessCallback = () => {},
    }) => {
      addToWishlist({
        wishlist: { ...targetWishlist },
        items: itemList.map((item) => ({
          quantity: item.quantity || 1,
          sku: item.sku,
          position: item.position || 0,
        })),
        onSuccessCallback,
        options: {
          flashMessage: true,
          undo: false,
          link: true,
        },
      });
    },
    [addToWishlist],
  );

  /**
   * Mutation to patch wishlist item
   * On change selection or quantity on an item
   */
  const {
    mutate: mutatePatchWishlistItems,
    isLoading: isLoadingPatchWishlistItems,
    isSuccess: isSuccessPatchWishlistItems,
  } = useMutation(patchWishlistItems);
  /**
   * On change selection or quantity on an item
   * @param {number} id
   * @param {boolean} isSelected
   * @param {number} quantity
   * @param {object} actions set of formik methods (setError ...)
   * @param {function} onSuccess
   */
  const onModifyItem = useCallback(
    ({ id, isSelected, quantity, actions, onSuccess }) => {
      mutatePatchWishlistItems(
        {
          // eslint-disable-next-line react/prop-types
          wishlist: { id: wishlist.id },
          item: { id, isSelected, quantity },
        },
        {
          onSuccess: (data) => {
            if (onSuccess) {
              onSuccess();
            } else {
              updateItem(data);

              toast.success(
                <Snack message={t('common:messages:forms.success.default')} />,
              );
            }
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              if (error.response.data.errors) {
                actions.setErrors(error.response.data.errors);
              }

              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutatePatchWishlistItems, t, updateItem, wishlist],
  );

  /**
   * Mutation to patch toggle selection all items
   */
  const {
    mutate: mutatePatchWishlistToggleSelection,
    isLoading: isLoadingPatchWishlistToggleSelection,
    isSuccess: isSuccessPatchWishlistToggleSelection,
  } = useMutation(patchWishlistToggleSelection);
  /**
   * On change selection or quantity on an item
   * @param {boolean} selected
   * @param {function} onSuccess
   */
  const onToggleSelect = useCallback(
    ({ selected, onSuccess = () => {} }) => {
      mutatePatchWishlistToggleSelection(
        {
          // eslint-disable-next-line react/prop-types
          wishlist: { id: wishlist.id },
          selected,
        },
        {
          onSuccess: (data) => {
            setLocalWishlist(data);
            onSuccess();

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutatePatchWishlistToggleSelection, wishlist, t],
  );

  const {
    mutate: mutateDeleteWishlistProducts,
    isLoading: isLoadingDeleteWishlistProducts,
    isSuccess: isSuccessDeleteWishlistProducts,
  } = useMutation(deleteWishlistProduct);
  /**
   * Remove product(s) to wishlist
   *
   * @param {number} id id of wishlist
   * @param {array} products list of products
   * @param {function} onSuccess
   */
  const onRemoveProduct = useCallback(
    ({ id, item, onSuccess = () => {} }) => {
      mutateDeleteWishlistProducts(
        {
          id,
          item,
        },
        {
          onSuccess: () => {
            setLocalWishlist({
              ...localWishlist,
              items: localWishlist.items.filter(
                (product) => product.id !== item.id,
              ),
            });
            onSuccess();

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutateDeleteWishlistProducts, localWishlist, t],
  );

  const {
    mutate: mutatePutWishlist,
    isLoading: isLoadingPutWishlist,
    isSuccess: isSuccessPutWishlist,
  } = useMutation(putWishlist);

  /**
   * Update wishlist to name, position and category
   *
   * @param {object} item
   * @param {string} name
   * @param {function} onError
   * @param {function} onSuccess
   */
  const onUpdateName = useCallback(
    ({ item, name, onError = () => {}, onSuccess = () => {} }) => {
      mutatePutWishlist(
        {
          id: item.id,
          name,
        },
        {
          onSuccess: (data) => {
            onSuccess(data);
            setLocalWishlist(data);

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            onError(error);

            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else if (error.response.status === 400) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.name_too_long')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutatePutWishlist, t],
  );

  const {
    mutate: mutateDeleteWishlist,
    isLoading: isLoadingDeleteWishlist,
  } = useMutation(deleteWishlist);

  /**
   * Deleted wishlist
   * @param {number} id
   * @param {function} onSuccess
   * @type {(function({id: *, onSuccess?: *}): void)|*}
   */
  const onRemoveWishlist = useCallback(
    ({ id, onSuccess = () => {} }) => {
      mutateDeleteWishlist(id, {
        onSuccess: () => {
          onSuccess();
        },
        onError: (error) => {
          if (error.response.status === 500) {
            toast.error(
              <Snack
                message={t('common:messages:forms.errors.server_error')}
              />,
            );
          } else {
            toast.error(
              <Snack message={t('common:messages:forms.errors.default')} />,
            );
          }
        },
      });
    },
    [t, mutateDeleteWishlist],
  );

  const {
    mutate: mutatePatchWishlist,
    isLoading: isLoadingPatchWishlist,
  } = useMutation(patchWishlist);

  /**
   * Update position of wishlist items
   */
  const onSaveSorting = useCallback(
    ({ onSuccess = () => {} }) => {
      mutatePatchWishlist(
        {
          wishlist: { id: localWishlist.id },
          items: localWishlist.items.map((item, index) => ({
            id: item.id,
            position: index,
          })),
        },
        {
          onSuccess: (data) => {
            onSuccess(data);
            setLocalWishlist(data);
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [localWishlist, mutatePatchWishlist, t],
  );

  /**
   * Check if isSuccess
   */
  const isSuccessWishlist = useMemo(
    () =>
      isSuccessDeleteWishlistProducts ||
      isSuccessPatchWishlistItems ||
      isSuccessPatchWishlistToggleSelection ||
      isSuccessPutWishlist,
    [
      isSuccessDeleteWishlistProducts,
      isSuccessPatchWishlistItems,
      isSuccessPatchWishlistToggleSelection,
      isSuccessPutWishlist,
    ],
  );

  /**
   * Check if action is disabled
   * @type {boolean}
   */
  const isDisabled = useMemo(
    () => ({
      loading:
        isLoading ||
        isLoadingAddToWishlist ||
        isLoadingDeleteWishlist ||
        isLoadingDeleteWishlistProducts ||
        isLoadingPatchWishlist ||
        isLoadingPatchWishlistItems ||
        isLoadingPatchWishlistToggleSelection ||
        isLoadingPutWishlist,
      disabled:
        disabled ||
        isLoading ||
        isLoadingAddToWishlist ||
        isLoadingDeleteWishlist ||
        isLoadingDeleteWishlistProducts ||
        isLoadingPatchWishlist ||
        isLoadingPatchWishlistItems ||
        isLoadingPatchWishlistToggleSelection,
    }),
    [
      disabled,
      isLoading,
      isLoadingAddToWishlist,
      isLoadingDeleteWishlist,
      isLoadingDeleteWishlistProducts,
      isLoadingPatchWishlist,
      isLoadingPatchWishlistItems,
      isLoadingPatchWishlistToggleSelection,
      isLoadingPutWishlist,
    ],
  );

  return {
    isDisabled,
    isSuccess: isSuccessWishlist,
    isUpdateOrderNeeded,
    localWishlist,
    onAddProducts,
    onAddProductsToWishlist,
    onModifyItem,
    onRemoveProduct,
    onRemoveWishlist,
    onSaveSorting,
    onSetLocalWishlist: setLocalWishlist,
    onToggleSelect,
    onUpdateName,
  };
};

export default useWishlist;
